import React, { useEffect, useState } from 'react';
import { useBusinessStore, HardwareScanner } from '@/stores/useBusinessStore';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    X, Loader2, ScanBarcode, Plus,
    Trash2, Edit2, Play, Pause, AlertCircle, Save
} from 'lucide-react';

const scannerSchema = z.object({
    name: z.string().min(1, 'Scanner name is required'),
    device_identifier: z.string().min(1, 'Device identifier is required'),
    status: z.enum(['active', 'inactive', 'maintenance']).default('active')
});

type ScannerFormData = z.infer<typeof scannerSchema>;

interface ScannerManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ScannerManagementModal: React.FC<ScannerManagementModalProps> = ({ isOpen, onClose }) => {
    const {
        activeBusiness, scanners, loading,
        fetchScanners, addScanner, updateScanner, deleteScanner
    } = useBusinessStore();

    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
        resolver: zodResolver(scannerSchema),
        defaultValues: { status: 'active' }
    });

    useEffect(() => {
        if (isOpen && activeBusiness) {
            fetchScanners(activeBusiness.id);
            setIsAdding(false);
            setEditingId(null);
            reset();
        }
    }, [isOpen, activeBusiness, fetchScanners, reset]);

    const handleCreate = async (data: ScannerFormData) => {
        if (!activeBusiness) return;
        try {
            await addScanner(activeBusiness.id, data);
            setIsAdding(false);
            reset();
        } catch (error) {
            console.error('Failed to add scanner:', error);
        }
    };

    const handleUpdate = async (data: ScannerFormData) => {
        if (!activeBusiness || !editingId) return;
        try {
            await updateScanner(activeBusiness.id, editingId, data);
            setEditingId(null);
            reset();
        } catch (error) {
            console.error('Failed to update scanner:', error);
        }
    };

    const handleDelete = async (scannerId: string) => {
        if (!activeBusiness || !confirm("Are you sure you want to remove this scanner?")) return;
        try {
            await deleteScanner(activeBusiness.id, scannerId);
        } catch (error) {
            console.error('Failed to delete scanner:', error);
        }
    };

    const startEditing = (scanner: HardwareScanner) => {
        setEditingId(scanner.id);
        setIsAdding(false);
        setValue('name', scanner.name);
        setValue('device_identifier', scanner.device_identifier);
        setValue('status', scanner.status);
    };

    const cancelForm = () => {
        setIsAdding(false);
        setEditingId(null);
        reset();
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active': return <Play size={14} className="text-green-500" />;
            case 'inactive': return <Pause size={14} className="text-light-subtle dark:text-dark-subtle" />;
            case 'maintenance': return <AlertCircle size={14} className="text-yellow-500" />;
            default: return null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-light-card dark:bg-dark-card/90 backdrop-blur-md w-full max-w-3xl rounded-xl shadow-2xl border border-light-border dark:border-dark-border/50 flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-light-border dark:border-dark-border bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 dark:bg-gray-100 dark:bg-gray-800/10">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <ScanBarcode className="text-primary" size={24} />
                        Hardware Scanners
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 dark:bg-gray-100 dark:bg-gray-800 rounded-full transition-colors">
                        <X size={20} className="text-light-subtle dark:text-dark-subtle" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Action Bar */}
                    <div className="flex justify-between items-center">
                        <p className="text-sm text-light-subtle dark:text-dark-subtle">
                            Manage physical barcode readers linked to your store.
                        </p>
                        {!isAdding && !editingId && (
                            <button
                                onClick={() => setIsAdding(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition"
                            >
                                <Plus size={16} />
                                Add Scanner
                            </button>
                        )}
                    </div>

                    {/* Edit or Create Form */}
                    {(isAdding || editingId) && (
                        <div className="bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 dark:bg-gray-100 dark:bg-gray-800/30 border border-light-border dark:border-dark-border rounded-xl p-5 mb-6">
                            <h3 className="font-semibold mb-4 text-light-text dark:text-dark-text flex items-center gap-2">
                                {isAdding ? <><Plus size={18} /> Register New Scanner</> : <><Edit2 size={18} /> Edit Scanner</>}
                            </h3>
                            <form onSubmit={handleSubmit((data) => editingId ? handleUpdate(data as ScannerFormData) : handleCreate(data as ScannerFormData))} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase">Scanner Name</label>
                                        <input
                                            {...register('name')}
                                            placeholder="e.g. Front Desk Zebra"
                                            className="w-full px-3 py-2 bg-background border border-light-border dark:border-dark-border rounded-lg outline-none focus:border-primary"
                                        />
                                        {errors.name && <p className="text-danger dark:text-danger-dark text-xs">{errors.name.message}</p>}
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase">Hardware ID / MAC</label>
                                        <input
                                            {...register('device_identifier')}
                                            placeholder="e.g. SN-987654321"
                                            className="w-full px-3 py-2 bg-background border border-light-border dark:border-dark-border rounded-lg outline-none focus:border-primary"
                                        />
                                        {errors.device_identifier && <p className="text-danger dark:text-danger-dark text-xs">{errors.device_identifier.message}</p>}
                                    </div>
                                </div>
                                <div className="space-y-1.5 w-full md:w-1/2">
                                    <label className="text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase">Operating Status</label>
                                    <select
                                        {...register('status')}
                                        className="w-full px-3 py-2 bg-background border border-light-border dark:border-dark-border rounded-lg outline-none focus:border-primary"
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="maintenance">Maintenance</option>
                                    </select>
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={cancelForm}
                                        className="px-4 py-2 text-sm font-medium border border-light-border dark:border-dark-border text-light-text dark:text-dark-text hover:bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 dark:bg-gray-100 dark:bg-gray-800 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                                    >
                                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                        {isAdding ? "Save Scanner" : "Update Settings"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Scanner List */}
                    <div className="space-y-3">
                        {loading && !isAdding && !editingId && scanners.length === 0 ? (
                            <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-primary" size={24} /></div>
                        ) : scanners.length === 0 ? (
                            <div className="text-center py-12 border border-dashed border-light-border dark:border-dark-border rounded-xl">
                                <ScanBarcode size={32} className="mx-auto text-light-subtle dark:text-dark-subtle mb-3 opacity-50" />
                                <p className="text-light-subtle dark:text-dark-subtle font-medium">No hardware scanners registered</p>
                            </div>
                        ) : (
                            scanners.map((scanner) => (
                                <div
                                    key={scanner.id}
                                    className={`flex items-center justify-between p-4 rounded-xl border transition-all ${editingId === scanner.id ? 'border-primary bg-primary/5' : 'border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card hover:bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 dark:bg-gray-100 dark:bg-gray-800/30'
                                        }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-lg flex items-center justify-center ${scanner.status === 'active' ? 'bg-green-500/10 text-green-500' :
                                            scanner.status === 'maintenance' ? 'bg-yellow-500/10 text-yellow-500' :
                                                'bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 dark:bg-gray-100 dark:bg-gray-800 text-light-subtle dark:text-dark-subtle'
                                            }`}>
                                            <ScanBarcode size={24} />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-light-text dark:text-dark-text flex items-center gap-2">
                                                {scanner.name}
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize flex items-center gap-1 font-medium ${scanner.status === 'active' ? 'bg-green-500/10 text-green-600' :
                                                    scanner.status === 'maintenance' ? 'bg-yellow-500/10 text-yellow-600' :
                                                        'bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 dark:bg-gray-100 dark:bg-gray-800 text-light-subtle dark:text-dark-subtle'
                                                    }`}>
                                                    {getStatusIcon(scanner.status)}
                                                    {scanner.status}
                                                </span>
                                            </h4>
                                            <div className="flex items-center gap-3 text-xs text-light-subtle dark:text-dark-subtle mt-1">
                                                <span>ID: <code className="bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 dark:bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{scanner.device_identifier}</code></span>
                                                <span>• Added: {new Date(scanner.created_at).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => startEditing(scanner)}
                                            disabled={editingId === scanner.id}
                                            className="p-2 text-light-subtle dark:text-dark-subtle hover:text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                                            title="Edit Scanner"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(scanner.id)}
                                            className="p-2 text-light-subtle dark:text-dark-subtle hover:text-danger dark:text-danger-dark hover:bg-danger/10 dark:bg-danger-dark/10 rounded-lg transition-colors"
                                            title="Remove Scanner"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
